import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Share2, Copy } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Inline brand glyphs (keeps bundle small + uses correct brand colors)
const WAIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12.04 2C6.55 2 2.1 6.45 2.1 11.94c0 2.1.55 4.15 1.6 5.96L2 22l4.22-1.1a9.93 9.93 0 0 0 5.82 1.84h.01c5.49 0 9.94-4.45 9.94-9.94S17.53 2 12.04 2zm5.8 14.06c-.24.69-1.42 1.32-1.96 1.4-.5.08-1.13.11-1.83-.11-.42-.13-.96-.31-1.66-.6-2.93-1.27-4.84-4.21-4.99-4.4-.15-.2-1.19-1.58-1.19-3.02 0-1.43.75-2.13 1.02-2.43.27-.3.59-.38.79-.38.2 0 .39.01.57.01.18.01.43-.07.67.51.24.59.83 2.03.91 2.18.07.15.12.32.02.52-.09.19-.14.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.18.29.78 1.29 1.67 2.09 1.15 1.02 2.12 1.34 2.42 1.49.3.15.47.13.65-.08.18-.21.74-.86.94-1.16.2-.29.4-.24.67-.15.27.1 1.71.81 2 .96.29.15.49.22.56.34.07.13.07.74-.17 1.43z"/></svg>
);
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.25 2.25H8.08l4.713 6.231zM17.083 19.77h1.833L7.084 4.126H5.117z"/></svg>
);
const FBIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396z"/></svg>
);
const TGIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
);

const SocialShareBar = ({ url, title, text, compact = false }) => {
  const { t } = useTranslation();
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = text || title || 'Check out ShipitNow — delivery in minutes';
  const enc = encodeURIComponent;

  const targets = [
    { id: 'wa', label: 'WhatsApp', color: '#25D366',
      href: `https://wa.me/?text=${enc(shareText + '\n' + fullUrl)}`, Icon: WAIcon },
    { id: 'x', label: 'X (Twitter)', color: '#000000',
      href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(fullUrl)}`, Icon: XIcon },
    { id: 'fb', label: 'Facebook', color: '#1877F2',
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(fullUrl)}&quote=${enc(shareText)}`, Icon: FBIcon },
    { id: 'tg', label: 'Telegram', color: '#26A5E4',
      href: `https://t.me/share/url?url=${enc(fullUrl)}&text=${enc(shareText)}`, Icon: TGIcon },
  ];

  const open = (href) => window.open(href, 'shareWindow', 'width=560,height=620,noopener,noreferrer');
  const copy = async () => {
    try { await navigator.clipboard.writeText(fullUrl); toast.success(t('common.copied', 'Link copied')); }
    catch { toast.error('Could not copy link'); }
  };
  const nativeShare = async () => {
    try { if (navigator.share) await navigator.share({ url: fullUrl, title, text: shareText }); else copy(); }
    catch { /* user cancelled */ }
  };

  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="social-share-trigger"
            className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 h-9 border border-slate-200 bg-white hover:border-slate-300">
            <Share2 className="h-4 w-4 text-slate-500" />
            <span className="font-medium text-slate-700">{t('common.share', 'Share')}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {targets.map((s) => (
            <DropdownMenuItem key={s.id} onClick={() => open(s.href)} className="cursor-pointer" data-testid={`share-${s.id}`}>
              <s.Icon className="h-4 w-4 mr-2.5" style={{ color: s.color }} />
              <span>{s.label}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={nativeShare} className="cursor-pointer" data-testid="share-native">
            <Share2 className="h-4 w-4 mr-2.5 text-slate-500" /> Device share…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copy} className="cursor-pointer" data-testid="share-copy">
            <Copy className="h-4 w-4 mr-2.5 text-slate-500" /> {t('common.copy_link', 'Copy link')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Expanded variant — pill row of platform icons
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="social-share-bar">
      {targets.map((s) => (
        <button key={s.id} onClick={() => open(s.href)} title={`Share on ${s.label}`}
          className="h-9 w-9 rounded-full border border-slate-200 bg-white hover:border-slate-300 flex items-center justify-center transition-colors"
          style={{ color: s.color }} data-testid={`share-${s.id}`}>
          <s.Icon className="h-4 w-4" />
        </button>
      ))}
      <button onClick={copy} title="Copy link"
        className="h-9 w-9 rounded-full border border-slate-200 bg-white hover:border-slate-300 flex items-center justify-center text-slate-500" data-testid="share-copy">
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
};

export default SocialShareBar;
